import { startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiRequest } from '../utils/api';
import { useChatState } from '../contexts/ChatStateContext';

/**
 * Custom hook for managing analysis state operations
 * Handles property changes, loading saved analyses, and basic state management
 */
export const useAnalysisState = (appData, setAppData, isOnboarding = false) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 🎯 CHAT STATE CONTEXT - For updating chat state from property switch responses
  const { updateFromBackend } = useChatState();
  
  // 🏠 PROPERTY CHANGE HANDLER
  const handlePropertyChange = async (newProperty) => {
    try {
      console.log('🎯 [useAnalysisState] Property change with onboarding state:', isOnboarding);
      const response = await apiRequest('/api/analyze/gsc/switch-property/', {
        method: 'POST',
        body: JSON.stringify({ 
          property: newProperty,
          is_onboarding: isOnboarding
        })
      });
      const data = await response.json();
      
      console.log('🎯 [useAnalysisState] Property switch response:', {
        selectedProperty: data.selectedProperty,
        hasChatState: !!data.chat_state,
        chatState: data.chat_state
      });

      // 🎯 UPDATE CHAT STATE: Property switch endpoint provides updated chat state
      if (data.chat_state) {
        console.log('🎯 [useAnalysisState] Updating chat state from property switch:', data.chat_state);
        updateFromBackend(data.chat_state);
      } else {
        console.log('⚠️ [useAnalysisState] No chat_state in property switch response');
      }
      
      // Use startTransition to batch all updates together
      startTransition(() => {
        setAppData(prev => ({
          ...prev,
          selectedProperty: data.selectedProperty,
          temporaryAnalysis: data.temporaryAnalysis
        }));
      });
      
    } catch (error) {
      throw error; // Re-throw so caller can handle UI feedback
    }
  };

  // 📂 LOAD SAVED ANALYSIS HANDLER
  const handleLoadSavedAnalysis = async (analysisId) => {
    try {
      const response = await apiRequest(`/api/analyze/saved/get/${analysisId}/`);
      if (!response.ok) throw new Error('Failed to load saved analysis');
      
      const data = await response.json();
      
      // Use startTransition to batch all updates together
      startTransition(() => {
        setAppData(prev => ({
          ...prev,
          temporaryAnalysis: data.analysis,
          // Optionally update selectedProperty if the analysis is for a different property
          selectedProperty: data.selectedProperty || prev.selectedProperty
        }));
      });
      
    } catch (error) {
      throw error; // Re-throw so caller can handle UI feedback
    }
  };

  // 🗑️ CLEAR ANALYSIS HANDLER
  const handleClearAnalysis = () => {
    startTransition(() => {
      setAppData(prev => ({
        ...prev,
        temporaryAnalysis: null
      }));
    });
  };

  return {
    handlePropertyChange,
    handleLoadSavedAnalysis,
    handleClearAnalysis
  };
}; 